import { useState, useEffect } from 'react';

/**
 * Hook to detect if the user has enabled "reduce motion" in their OS settings.
 * This is critical for accessibility - some users experience motion sickness
 * or have vestibular disorders that make animations problematic.
 * 
 * @returns true if user prefers reduced motion, false otherwise
 */
export function useReducedMotion(): boolean {
    const [reducedMotion, setReducedMotion] = useState<boolean>(() => {
        // Server-side rendering safety check
        if (typeof window === 'undefined') return false;
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    });

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        const handler = (event: MediaQueryListEvent) => {
            setReducedMotion(event.matches);
        };

        // Listen for changes (user might toggle preference while app is open)
        mediaQuery.addEventListener('change', handler);

        return () => {
            mediaQuery.removeEventListener('change', handler);
        };
    }, []);

    return reducedMotion;
}

export default useReducedMotion;
